import { useState } from "react";
import {
  Check,
  Droplet,
  Sparkles,
  CalendarHeart,
  Crown,
  ShieldCheck,
  Diamond,
  Clock,
  Star,
  ChevronDown,
} from "lucide-react";

const GOLD = "#B89968";
const GOLD_SOFT = "#D8C39A";
const INK = "#161618";
const IVORY = "#F1EDDE";

const TIERS = [
  {
    id: "preserve",
    name: "The Preserve",
    kind: "Plan",
    price: 85,
    paymentLink: "https://buy.stripe.com/eVqfZj5H97bY92uaWX43S0n",
    items: [
      { icon: Sparkles, label: "3 × Anti-Wrinkle, 3 areas", sub: "approx. every 4 months" },
      { icon: Droplet, label: "1 × Microneedling session" },
    ],
    footnote: "Plus member pricing on additional treatments",
    perks: [
      "Priority booking",
      "Exclusive member rates",
      "Ongoing treatment planning",
      "Support between appointments",
    ],
  },
  {
    id: "essential",
    name: "The Restore",
    kind: "Plan",
    price: 110,
    featured: true,
    paymentLink: "https://buy.stripe.com/3cI8wRd9BfIudiK2qr43S0m",
    items: [
      { icon: Sparkles, label: "3 × Anti-Wrinkle, 3 areas", sub: "approx. every 4 months" },
      { icon: Droplet, label: "2 × Microneedling sessions" },
      { icon: CalendarHeart, label: "1ml Dermal Filler", sub: "lips, cheeks, chin or jawline" },
    ],
    perks: [
      "Priority booking",
      "Exclusive member rates",
      "Ongoing treatment planning",
      "Support between appointments",
    ],
  },
  {
    id: "signature",
    name: "The Signature",
    kind: "Membership",
    price: 140,
    paymentLink: "https://buy.stripe.com/eVq9AVedFcwi6Ume9943S0l",
    items: [
      { icon: Sparkles, label: "3 × Anti-Wrinkle, 3 areas", sub: "approx. every 4 months" },
      { icon: Droplet, label: "2 × Microneedling sessions" },
      { icon: CalendarHeart, label: "2ml Dermal Filler", sub: "lips, cheeks, chin or jawline" },
      { icon: Diamond, label: "2 × Skin or Eye Boosters" },
    ],
    perks: [
      "Priority booking",
      "Exclusive member rates",
      "Ongoing treatment planning",
      "Support between appointments",
      "VIP treatment experience",
    ],
  },
];

const WHY_ITEMS = [
  { icon: CalendarHeart, label: "Easy monthly payments" },
  { icon: Sparkles, label: "Consistent, natural results" },
  { icon: Diamond, label: "Long-term skin health & confidence" },
  { icon: Clock, label: "Priority booking" },
  { icon: Star, label: "Exclusive member benefits" },
];

export const Membership = () => {
  const [hovered, setHovered] = useState(null);
  const [termsOpen, setTermsOpen] = useState(false);

  const go = (tier) => {
    if (tier.paymentLink) {
      window.open(tier.paymentLink, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <section
      id="membership"
      data-testid="membership-section"
      className="relative py-16 lg:py-24 overflow-hidden"
      style={{ background: IVORY }}
    >
      {/* Stacy portrait watermark — top-left */}
      <div
        aria-hidden
        className="absolute top-0 left-0 w-[300px] sm:w-[380px] lg:w-[460px] pointer-events-none opacity-60"
      >
        <img
          src="/logos/stacy-head.png"
          alt=""
          className="block w-full h-auto"
          style={{
            maskImage:
              "linear-gradient(to right, black 60%, transparent 100%), linear-gradient(to bottom, black 75%, transparent 100%)",
            WebkitMaskImage:
              "linear-gradient(to right, black 60%, transparent 100%), linear-gradient(to bottom, black 75%, transparent 100%)",
            maskComposite: "intersect",
            WebkitMaskComposite: "source-in",
          }}
        />
      </div>

      {/* Member benefits badge — top-right */}
      <div
        aria-hidden
        className="absolute top-10 right-6 sm:right-10 hidden md:flex items-center justify-center"
      >
        <div
          className="rounded-full border flex flex-col items-center justify-center text-center"
          style={{
            background: INK,
            borderColor: GOLD,
            color: IVORY,
            width: 132,
            height: 132,
          }}
        >
          <span
            className="font-serif italic text-[0.65rem] tracking-[0.2em] uppercase"
            style={{ color: GOLD_SOFT }}
          >
            Exclusive
          </span>
          <span className="font-serif italic text-lg leading-tight" style={{ color: IVORY }}>
            Member
          </span>
          <span className="font-serif italic text-lg leading-tight" style={{ color: IVORY }}>
            Benefits
          </span>
          <span className="mt-1 text-xs" style={{ color: GOLD }}>♥</span>
        </div>
      </div>

      <div className="relative max-w-7xl mx-auto px-6 sm:px-10 pt-32 sm:pt-24 lg:pt-16">
        {/* Header */}
        <div className="text-center mb-10 reveal">
          <div
            className="text-[0.6rem] uppercase tracking-[0.32em] mb-3"
            style={{ color: GOLD }}
          >
            Stacy Fox · Laser &amp; Aesthetics
          </div>
          <h2
            className="font-serif font-light leading-[0.95]"
            style={{
              color: INK,
              fontSize: "clamp(2.5rem, 6vw, 4.75rem)",
              letterSpacing: "0.02em",
            }}
          >
            Membership Club
          </h2>
          <div
            className="mt-5 font-serif italic"
            style={{ color: GOLD, fontSize: "clamp(1.25rem, 2.4vw, 1.85rem)" }}
          >
            Invest in yourself. Glow all year.
          </div>
          <div className="flex items-center justify-center gap-3 mt-6">
            <span className="h-px w-12" style={{ background: GOLD }} />
            <span className="text-xs" style={{ color: GOLD }}>♥</span>
            <span className="h-px w-12" style={{ background: GOLD }} />
          </div>
          <p
            className="mt-7 text-[0.72rem] sm:text-sm uppercase tracking-[0.28em] leading-relaxed"
            style={{ color: INK }}
          >
            Beautiful results · Plan your year · Affordable monthly payments
          </p>
        </div>

        {/* Tier cards */}
        <div className="grid md:grid-cols-3 gap-6 lg:gap-8 mt-12">
          {TIERS.map((tier, i) => {
            const isFeatured = tier.featured;
            return (
              <article
                key={tier.id}
                data-testid={`membership-tier-${tier.id}`}
                onMouseEnter={() => setHovered(i)}
                onMouseLeave={() => setHovered(null)}
                onClick={() => go(tier)}
                className={`relative bg-white cursor-pointer transition-all duration-500 ${
                  hovered === i ? "-translate-y-2 shadow-2xl" : "shadow-md"
                } ${isFeatured ? "md:-translate-y-3" : ""}`}
                style={{
                  border: `1px solid ${isFeatured ? GOLD : "rgba(184,153,104,0.35)"}`,
                }}
              >
                {/* Black header */}
                <div
                  className="px-6 pt-8 pb-6 text-center"
                  style={{
                    background: INK,
                    color: IVORY,
                    clipPath: "polygon(0 0, 100% 0, 100% calc(100% - 18px), 50% 100%, 0 calc(100% - 18px))",
                  }}
                >
                  <div
                    className="text-[0.55rem] uppercase tracking-[0.3em] mb-2"
                    style={{ color: GOLD_SOFT }}
                  >
                    The
                  </div>
                  <h3 className="font-serif text-3xl sm:text-4xl tracking-wide leading-tight">
                    {tier.name.replace(/^The\s+/, "").toUpperCase()}
                  </h3>
                  <div
                    className="font-serif italic text-base mt-1"
                    style={{ color: GOLD_SOFT }}
                  >
                    {tier.kind}
                  </div>
                </div>

                {/* Price strip */}
                <div
                  className="text-center py-5"
                  style={{ background: GOLD_SOFT }}
                >
                  <div
                    className="font-serif text-5xl sm:text-6xl"
                    style={{ color: INK, fontWeight: 400 }}
                  >
                    £{tier.price}
                  </div>
                  <div
                    className="text-[0.6rem] uppercase tracking-[0.32em] mt-1"
                    style={{ color: INK }}
                  >
                    Per month
                  </div>
                </div>

                {/* Includes yearly */}
                <div className="px-7 pt-8 pb-6 text-center">
                  <div
                    className="text-[0.6rem] uppercase tracking-[0.3em] mb-6"
                    style={{ color: GOLD }}
                  >
                    Includes yearly:
                  </div>
                  <ul className="space-y-6 text-left">
                    {tier.items.map((it, idx) => {
                      const Icon = it.icon;
                      return (
                        <li key={idx} className="flex items-start gap-4">
                          <span
                            className="shrink-0 w-10 h-10 rounded-full flex items-center justify-center"
                            style={{ background: IVORY, color: GOLD }}
                          >
                            <Icon className="w-5 h-5" strokeWidth={1.5} />
                          </span>
                          <div className="flex-1">
                            <div
                              className="text-[0.78rem] uppercase tracking-[0.18em] leading-snug"
                              style={{ color: INK }}
                            >
                              {it.label}
                            </div>
                            {it.sub && (
                              <div
                                className="text-[0.65rem] uppercase tracking-[0.18em] mt-1"
                                style={{ color: "#8C8580" }}
                              >
                                {it.sub}
                              </div>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                  {tier.footnote && (
                    <p
                      className="mt-7 pt-5 text-[0.66rem] uppercase tracking-[0.22em] leading-relaxed border-t"
                      style={{ color: INK, borderColor: "rgba(184,153,104,0.3)" }}
                    >
                      {tier.footnote}
                    </p>
                  )}
                </div>

                {/* Member benefits dark block */}
                <div
                  className="mx-5 mb-6 rounded-md px-6 py-6 text-left"
                  style={{ background: INK, color: IVORY }}
                >
                  <div
                    className="text-[0.6rem] uppercase tracking-[0.3em] mb-3"
                    style={{ color: GOLD_SOFT }}
                  >
                    Member Benefits:
                  </div>
                  <ul className="space-y-2">
                    {tier.perks.map((p) => (
                      <li key={p} className="flex gap-3 items-start text-[0.78rem]">
                        <Check
                          className="w-3.5 h-3.5 mt-1 shrink-0"
                          style={{ color: GOLD }}
                          strokeWidth={2}
                        />
                        <span style={{ color: IVORY }}>{p}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* CTA */}
                <div className="px-5 pb-7">
                  <button
                    data-testid={`membership-select-${tier.id}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      go(tier);
                    }}
                    className="block w-full text-center py-3.5 text-[0.7rem] uppercase tracking-[0.28em] font-medium transition-colors"
                    style={{
                      background: isFeatured ? INK : "transparent",
                      color: isFeatured ? IVORY : INK,
                      border: `1px solid ${INK}`,
                    }}
                  >
                    Subscribe · £{tier.price}/mo
                  </button>
                </div>

                <div
                  className="h-1 w-full"
                  style={{ background: GOLD }}
                />
              </article>
            );
          })}
        </div>

        {/* Why memberships work */}
        <div className="mt-20 reveal">
          <div className="flex items-center justify-center gap-4 mb-10">
            <span className="h-px w-12" style={{ background: GOLD }} />
            <h4
              className="text-[0.7rem] uppercase tracking-[0.32em]"
              style={{ color: INK }}
            >
              Why Memberships Work
            </h4>
            <span className="h-px w-12" style={{ background: GOLD }} />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-8">
            {WHY_ITEMS.map((w) => {
              const Icon = w.icon;
              return (
                <div key={w.label} className="text-center">
                  <Icon
                    className="w-10 h-10 mx-auto mb-3"
                    style={{ color: INK }}
                    strokeWidth={1.5}
                  />
                  <div
                    className="text-[0.6rem] uppercase tracking-[0.22em] leading-snug"
                    style={{ color: INK }}
                  >
                    {w.label}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Important info & footer note */}
        <div
          className="mt-20 grid md:grid-cols-3 gap-0 reveal"
          style={{ background: INK, color: IVORY }}
        >
          <div className="p-8 md:p-10">
            <div
              className="text-[0.65rem] uppercase tracking-[0.3em] mb-4"
              style={{ color: GOLD_SOFT }}
            >
              Important Information
            </div>
            <ul className="space-y-2 text-[0.78rem]">
              {[
                "12 month minimum term",
                "Payments are non-refundable",
                "Treatments must be used within your membership year",
                "Non-transferable membership",
              ].map((t) => (
                <li key={t} className="flex gap-3">
                  <Check
                    className="w-3.5 h-3.5 mt-1 shrink-0"
                    style={{ color: GOLD }}
                    strokeWidth={2}
                  />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="p-8 md:p-10 border-t md:border-t-0 md:border-l" style={{ borderColor: "#2A2A2D" }}>
            <div
              className="text-[0.65rem] uppercase tracking-[0.3em] mb-4"
              style={{ color: GOLD_SOFT }}
            >
              Please Note
            </div>
            <p className="text-[0.78rem] leading-relaxed">
              All treatments are subject to consultation, medical suitability
              and prescriber approval where required. Results and treatment
              plans may vary from person to person.
            </p>
          </div>
          <div className="p-8 md:p-10 border-t md:border-t-0 md:border-l flex flex-col items-center justify-center text-center" style={{ borderColor: "#2A2A2D" }}>
            <ShieldCheck className="w-8 h-8 mb-3" style={{ color: GOLD }} strokeWidth={1.4} />
            <div
              className="font-serif italic text-lg"
              style={{ color: IVORY }}
            >
              Enhancing confidence,
            </div>
            <div
              className="font-serif italic text-lg"
              style={{ color: GOLD }}
            >
              naturally.
            </div>
          </div>
        </div>

        {/* Ready to join footer */}
        <div className="mt-10 flex flex-col md:flex-row items-center justify-between gap-6 text-center md:text-left">
          <div className="flex items-center gap-3">
            <Crown className="w-5 h-5" style={{ color: GOLD }} strokeWidth={1.5} />
            <span
              className="text-[0.7rem] uppercase tracking-[0.3em]"
              style={{ color: INK }}
            >
              Ready to join?
            </span>
          </div>
          <span
            className="text-[0.7rem] uppercase tracking-[0.28em]"
            style={{ color: "#8C8580" }}
          >
            Choose your plan &amp; join today
          </span>
          <span
            className="font-serif italic"
            style={{ color: INK, fontSize: "1.05rem" }}
          >
            Your best skin is our plan.
          </span>
        </div>
        <p
          className="mt-4 text-[0.62rem] text-center"
          style={{ color: "#8C8580" }}
        >
          All prices are per month. Card payments are processed securely via Stripe.
        </p>

        {/* Membership Terms & Conditions — expandable */}
        <div className="mt-12 reveal" data-testid="membership-terms-wrapper">
          <button
            type="button"
            onClick={() => setTermsOpen((v) => !v)}
            data-testid="membership-terms-toggle"
            aria-expanded={termsOpen}
            aria-controls="membership-terms-panel"
            className="w-full flex items-center justify-between gap-4 px-6 sm:px-8 py-5 border transition-colors"
            style={{
              background: termsOpen ? INK : "transparent",
              color: termsOpen ? IVORY : INK,
              borderColor: INK,
            }}
          >
            <span className="text-left">
              <span
                className="block text-[0.6rem] uppercase tracking-[0.3em]"
                style={{ color: termsOpen ? GOLD_SOFT : GOLD }}
              >
                Before you join
              </span>
              <span
                className="block font-serif text-xl sm:text-2xl mt-1"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                Membership Terms &amp; Conditions
              </span>
            </span>
            <ChevronDown
              className={`w-5 h-5 shrink-0 transition-transform duration-300 ${
                termsOpen ? "rotate-180" : ""
              }`}
              strokeWidth={1.5}
            />
          </button>

          {termsOpen && (
            <div
              id="membership-terms-panel"
              data-testid="membership-terms-panel"
              className="border border-t-0 px-6 sm:px-10 py-10 sm:py-12 bg-white"
              style={{ borderColor: INK }}
            >
              <MembershipTermsContent />
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

const TermsH3 = ({ children }) => (
  <h3
    className="font-serif text-xl sm:text-2xl text-[#161618] mt-10 mb-4 tracking-tight"
    style={{ fontFamily: "'Cormorant Garamond', serif" }}
  >
    {children}
  </h3>
);

const TermsP = ({ children }) => (
  <p className="text-[#3a3a3c] font-light leading-relaxed text-[0.95rem] mb-4">
    {children}
  </p>
);

const TermsList = ({ items }) => (
  <ul className="space-y-2 mb-5 pl-1">
    {items.map((it, i) => (
      <li
        key={i}
        className="flex items-start gap-3 text-[#3a3a3c] font-light leading-relaxed text-[0.95rem]"
      >
        <span className="mt-2 inline-block w-1 h-1 rounded-full bg-[#B6AA96] flex-shrink-0" />
        <span>{it}</span>
      </li>
    ))}
  </ul>
);

const MembershipTermsContent = () => (
  <div data-testid="membership-terms-content" className="max-w-3xl mx-auto">
    <div className="text-[0.6rem] uppercase tracking-[0.32em] text-[#B6AA96] mb-2">
      Stacy Fox Laser &amp; Advanced Aesthetics
    </div>
    <h2
      className="font-serif text-3xl sm:text-4xl text-[#161618] tracking-tight"
      style={{ fontFamily: "'Cormorant Garamond', serif" }}
    >
      Membership Plan Terms &amp; Conditions
    </h2>

    <TermsP>
      Thank you for joining the Stacy Fox Membership Plan. These terms outline
      how your membership works, what is included, and the responsibilities of
      both the clinic and the client.
    </TermsP>
    <TermsP>
      By joining the membership plan, you confirm that you have read,
      understood and agreed to these terms and conditions.
    </TermsP>

    <TermsH3>1. Membership Overview</TermsH3>
    <TermsP>
      Your membership is a structured 12-month treatment plan designed to
      support consistent maintenance, skin health and long-term aesthetic
      results.
    </TermsP>
    <TermsP>
      Membership plans are not a credit system, savings account or payment
      plan towards future treatments. Your monthly payments secure your place
      within a planned treatment programme, with specific treatments allocated
      at scheduled points throughout your membership period.
    </TermsP>
    <TermsP>
      Treatments are selected based on your chosen membership package and your
      individual suitability.
    </TermsP>
    <TermsP>
      All treatments remain subject to consultation, assessment and clinical
      suitability at the time of your appointment.
    </TermsP>

    <TermsH3>2. Membership Payments</TermsH3>
    <TermsP>Membership payments are processed securely through Stripe.</TermsP>
    <TermsP>
      By signing up to a membership plan, you authorise Stacy Fox Laser &amp;
      Advanced Aesthetics to collect your agreed monthly payment using the
      payment method provided.
    </TermsP>
    <TermsP>
      Stripe securely processes payments and stores payment details in
      accordance with their own security and compliance standards. Stacy Fox
      Laser &amp; Advanced Aesthetics does not have access to or store your
      full payment card details.
    </TermsP>
    <TermsP>
      Your membership must remain active and payments must be kept up to date
      for treatments to remain available.
    </TermsP>
    <TermsP>
      If a payment fails, your membership may be paused until payment is
      successfully received.
    </TermsP>
    <TermsP>During a paused membership:</TermsP>
    <TermsList
      items={[
        "No further membership treatments will be available",
        "Booking links may be suspended",
        "Your membership benefits will not continue until your account is brought up to date",
      ]}
    />

    <TermsH3>3. Membership Treatment Schedule</TermsH3>
    <TermsP>
      Treatments will become available according to your chosen plan and will
      be communicated via email.
    </TermsP>
    <TermsP>You will receive:</TermsP>
    <TermsList
      items={[
        "Details of your upcoming treatment",
        "When your treatment becomes available",
        "A personal booking link",
      ]}
    />
    <TermsP>
      This allows you to book appointments in advance at a time that works
      best with your schedule.
    </TermsP>
    <TermsP>
      Membership appointments should be planned ahead where possible to ensure
      availability.
    </TermsP>

    <TermsH3>4. Treatment Availability &amp; Expiry</TermsH3>
    <TermsP>
      Membership treatments are allocated specifically to the named client and
      are non-transferable.
    </TermsP>
    <TermsP>
      Treatments cannot be exchanged for cash, transferred to another person,
      or swapped for alternative treatments unless agreed clinically.
    </TermsP>
    <TermsP>
      Treatments must be used within the membership timeframe unless otherwise
      agreed.
    </TermsP>
    <TermsP>
      If a client chooses not to attend or delays treatment beyond the
      recommended timeframe, this may affect treatment outcomes and future
      scheduling.
    </TermsP>

    <TermsH3>5. Appointments, Cancellations &amp; Missed Appointments</TermsH3>
    <TermsP>Your appointment time is reserved specifically for you.</TermsP>
    <TermsP>
      Please provide as much notice as possible if you need to rearrange.
    </TermsP>
    <TermsP>
      Appointments cancelled with insufficient notice, missed appointments, or
      failure to attend may result in the treatment being forfeited depending
      on the circumstances.
    </TermsP>
    <TermsP>
      Repeated missed appointments may result in cancellation of your
      membership.
    </TermsP>

    <TermsH3>6. Clinical Suitability</TermsH3>
    <TermsP>
      All treatments are performed following consultation and assessment.
    </TermsP>
    <TermsP>
      Joining a membership does not guarantee a specific treatment if, at the
      time of consultation, it is deemed unsuitable or unsafe.
    </TermsP>
    <TermsP>Treatment decisions will always be based on:</TermsP>
    <TermsList
      items={[
        "Facial anatomy",
        "Skin condition",
        "Previous treatment history",
        "Medical information provided",
        "Safe clinical practice",
      ]}
    />
    <TermsP>
      Your safety and results will always come before completing a treatment
      schedule.
    </TermsP>

    <TermsH3>7. Anti-Wrinkle Treatments</TermsH3>
    <TermsP>
      Anti-wrinkle treatments include a scheduled review appointment
      approximately 2 weeks after treatment where clinically appropriate.
    </TermsP>
    <TermsP>
      Results vary between individuals and depend on factors including muscle
      activity, metabolism, lifestyle and previous treatment history.
    </TermsP>
    <TermsP>
      The aim of treatment is natural, balanced results rather than complete
      elimination of movement.
    </TermsP>

    <TermsH3>8. Client Responsibilities &amp; Medical Information</TermsH3>
    <TermsP>
      You agree to provide accurate and complete medical information before
      treatment.
    </TermsP>
    <TermsP>
      It is your responsibility to inform the clinic of any changes to:
    </TermsP>
    <TermsList
      items={[
        "Medical conditions",
        "Medication",
        "Pregnancy / breastfeeding status",
        "Allergies",
        "Previous aesthetic treatments",
      ]}
    />
    <TermsP>
      Failure to provide accurate information may affect your safety and
      treatment suitability.
    </TermsP>

    <TermsH3>9. Data Protection &amp; GDPR</TermsH3>
    <p
      className="font-serif italic text-[#161618] text-base mt-2 mb-3"
      style={{ fontFamily: "'Cormorant Garamond', serif" }}
    >
      How We Collect Your Information
    </p>
    <TermsP>
      To provide safe and effective treatments, we collect and process
      personal information including:
    </TermsP>
    <TermsList
      items={[
        "Name and contact details",
        "Appointment history",
        "Treatment records",
        "Medical information relevant to treatment safety",
        "Before and after photographs where consent has been provided",
        "Payment and membership information",
      ]}
    />
    <p
      className="font-serif italic text-[#161618] text-base mt-4 mb-3"
      style={{ fontFamily: "'Cormorant Garamond', serif" }}
    >
      How Your Information Is Used
    </p>
    <TermsP>Your information is used to:</TermsP>
    <TermsList
      items={[
        "Manage your membership",
        "Process payments",
        "Contact you regarding appointments and treatment availability",
        "Maintain accurate treatment records",
        "Provide safe clinical care",
        "Meet legal and professional obligations",
      ]}
    />
    <TermsP>
      Your information will never be sold or shared for marketing purposes
      without your consent.
    </TermsP>
    <p
      className="font-serif italic text-[#161618] text-base mt-4 mb-3"
      style={{ fontFamily: "'Cormorant Garamond', serif" }}
    >
      Storage &amp; Security of Information
    </p>
    <TermsP>
      Your personal information is stored securely using trusted systems used
      by Stacy Fox Laser &amp; Advanced Aesthetics.
    </TermsP>
    <TermsP>Payment information is processed securely through Stripe.</TermsP>
    <TermsP>
      We take appropriate steps to protect your information from loss, misuse,
      unauthorised access or disclosure.
    </TermsP>
    <p
      className="font-serif italic text-[#161618] text-base mt-4 mb-3"
      style={{ fontFamily: "'Cormorant Garamond', serif" }}
    >
      Your Rights
    </p>
    <TermsP>
      Under UK GDPR, you have rights regarding your personal data, including:
    </TermsP>
    <TermsList
      items={[
        "Requesting access to your information",
        "Requesting corrections",
        "Requesting deletion where appropriate",
        "Withdrawing consent where applicable",
      ]}
    />
    <TermsP>
      Requests can be made by contacting Stacy Fox Laser &amp; Advanced
      Aesthetics.
    </TermsP>

    <TermsH3>10. Communication Consent</TermsH3>
    <TermsP>
      By joining the membership, you agree to receive important service
      communications relating to:
    </TermsP>
    <TermsList
      items={[
        "Membership payments",
        "Appointment reminders",
        "Treatment availability",
        "Booking links",
        "Important updates regarding your plan",
      ]}
    />
    <TermsP>
      Marketing communications will only be sent where appropriate consent has
      been provided.
    </TermsP>

    <TermsH3>11. Membership Cancellation</TermsH3>
    <TermsP>Membership cancellation requests must be made in writing.</TermsP>
    <TermsP>
      Cancellation does not convert unused treatments into cash value or
      credit.
    </TermsP>
    <TermsP>
      Any outstanding payments, obligations or treatment restrictions will be
      managed according to these terms.
    </TermsP>

    <p
      className="mt-10 pt-8 border-t border-[#C9BEA9] font-serif italic text-[#161618] text-base sm:text-lg leading-relaxed"
      style={{ fontFamily: "'Cormorant Garamond', serif" }}
    >
      Thank you for trusting Stacy Fox Laser &amp; Advanced Aesthetics with
      your treatment journey.
    </p>
    <p className="mt-3 text-[#3a3a3c] font-light leading-relaxed text-[0.95rem]">
      Our priority is always safe, ethical and personalised care — helping you
      maintain your results through a considered approach to aesthetics.
    </p>
  </div>
);

export default Membership;
